import { useMemo, useState, useCallback } from 'react';

/**
 * IPT-556 이슈4 — 범용 클라이언트 테이블 정렬 유틸
 *
 * 어느 페이지의 어떤 표든 컬럼 헤더 클릭으로 오름/내림차순 정렬할 수 있도록 하는 공통 도구.
 * - 값 타입을 자동 감지: 숫자 / 날짜(ISO·yyyy-mm-dd) / 그 외 문자열(한글 우선 localeCompare)
 * - 빈 값(null/undefined/'')은 정렬 방향과 무관하게 항상 뒤로
 * - 원본 배열을 변형하지 않음(불변)
 */

export type SortDirection = 'asc' | 'desc';

const isEmpty = (v: unknown): boolean =>
  v === null || v === undefined || (typeof v === 'string' && v.trim() === '');

// '2026-06-26' 또는 '2026-06-26T...' 형태의 날짜 문자열 감지
const DATE_RE = /^\d{4}-\d{2}-\d{2}([T ].*)?$/;
const isDateString = (v: unknown): v is string =>
  typeof v === 'string' && DATE_RE.test(v.trim());

/** 단일 값 비교(방향 미적용). 빈 값은 항상 뒤로(=양수/음수 고정). */
export function compareValues(av: unknown, bv: unknown): number {
  const aEmpty = isEmpty(av);
  const bEmpty = isEmpty(bv);
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  // 숫자
  if (typeof av === 'number' && typeof bv === 'number') return av - bv;

  // 날짜 문자열
  if (isDateString(av) && isDateString(bv)) {
    return new Date(av).getTime() - new Date(bv).getTime();
  }

  // 숫자처럼 보이는 문자열(예: '1,234' / '42')
  const an = Number(String(av).replace(/,/g, ''));
  const bn = Number(String(bv).replace(/,/g, ''));
  if (!Number.isNaN(an) && !Number.isNaN(bn) && String(av).trim() !== '' && String(bv).trim() !== '') {
    return an - bn;
  }

  return String(av).localeCompare(String(bv), 'ko');
}

/** rows를 key/direction 기준으로 정렬한 새 배열 반환(불변). key가 없으면 원본 순서 유지. */
export function sortRows<T>(
  rows: T[],
  key: keyof T | null | undefined,
  direction: SortDirection,
): T[] {
  if (!key) return [...rows];
  const dir = direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const base = compareValues(a[key], b[key]);
    // 빈 값은 항상 뒤로(방향 무관) — base가 ±1 고정인 경우는 dir을 곱하지 않음
    const aEmpty = isEmpty(a[key]);
    const bEmpty = isEmpty(b[key]);
    if (aEmpty !== bEmpty) return base;
    return base * dir;
  });
}

/**
 * 표 정렬 상태 훅.
 *   const { sorted, sortKey, sortDir, onSort } = useTableSort(rows);
 *   <th onClick={() => onSort('name')}>이름 {sortIndicator(sortKey,'name',sortDir)}</th>
 *   {sorted.map(...)}
 */
export function useTableSort<T>(
  rows: T[],
  initialKey: keyof T | null = null,
  initialDir: SortDirection = 'asc',
) {
  // sortKey와 sortDir을 단일 상태로 관리 — setState 업데이터 안에서 다른 setState를
  // 호출하는 안티패턴을 피하고 단일 업데이트로 정렬 상태를 전환한다.
  const [sortState, setSortState] = useState<{ key: keyof T | null; dir: SortDirection }>({
    key: initialKey,
    dir: initialDir,
  });

  const onSort = useCallback((key: keyof T) => {
    setSortState((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' },
    );
  }, []);

  const sorted = useMemo(
    () => sortRows(rows, sortState.key, sortState.dir),
    [rows, sortState],
  );

  return { sorted, sortKey: sortState.key, sortDir: sortState.dir, onSort };
}

/** 헤더에 표시할 정렬 방향 인디케이터 문자열. */
export function sortIndicator<T>(
  sortKey: keyof T | null,
  columnKey: keyof T,
  sortDir: SortDirection,
): string {
  if (sortKey !== columnKey) return '↕';
  return sortDir === 'asc' ? '▲' : '▼';
}
